// cleanup_convert_to_typescript_file_GENERICS

/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import classnames from 'classnames';
import moment from 'moment';
import { kebabCase, template } from 'lodash';
import { JSSClasses } from 'proptypes/jss-prop-types';

import { ComposedGrid as Grid, defaultStrings } from '@glu/grid-react';
import { defaultStrings_en_US as dateStrings } from '@glu/datepicker-react';
import { defaultStrings_en_US as popoverStrings } from '@glu/popover-react';
import gluLocale from '@glu/locale';

import withWidget from 'components/workspace/withWidget';
import BasicResponseMessage from 'components/basicResponseMessage/BasicResponseMessage';
import FullscreenModal from 'components/fullscreenModal/FullscreenModal';
import GridActionModal from 'components/gridActionModal/GridActionModal';
import PrintLogo from 'components/print/PrintLogo';
import Snackbar from 'components/snackbar/Snackbar';
import GridHeader from 'components/gridHeader/GridHeader';
import useLocale from 'components/useLocale/useLocale';
import {
  ACTIONS_COLUMN,
  COLUMN_TYPES,
  DATE_TIME_FORMAT,
  DEFAULT_PER_PAGE,
  ON_GRID_READY,
  SAVED_VIEWS
} from 'constants.js';
import { isIE } from 'utils/browser';
import useWindowSize from 'hooks/useWindowSize';
import { fetchUserPreferences, saveUserPreferences } from 'api/workspace/service';

import {
  getPrintLoadingMsg,
  defaultStringComparator,
  limitPageCount,
  removeColumnsForPrint,
  logGridEvent,
  loggedGridSync,
  resetSavedViewsToFirstPage
} from './utils';
import {
  LIMIT_RECORDS_FOR_PRINT,
  FLITER_VALUE_GRANULARITY,
  EXCLUDE_FOR_EXPORT,
  LAYOUT_KEYS,
  EXTRA_PRINT_PROPS,
  SPACE_KEY_CODE
} from './basicGrid.constants';
import { mapColumnProps } from './BasicGrid.config';
import useStyles from './BasicGrid.styles';
import GetTrigger from './getTrigger';

/*
 * TODO: add static views.
 * see src/api/grid/SavedViewsCollection
 */

const fetchSavedViews = (key) =>
  fetchUserPreferences(key)
    .then(({ data, success }) => {
      const viewsPrefs = success ? JSON.parse(data.value) || [] : [];
      return resetSavedViewsToFirstPage(viewsPrefs);
    })
    .catch(() => []);

const saveView = ({ id, newViews }) => {
  const updatedSavedViews = resetSavedViewsToFirstPage(newViews);
  return saveUserPreferences(id, updatedSavedViews);
};

const NoRowsOverlayComponent = (props) => {
  const activeView = JSON.parse(sessionStorage.getItem(props.sessionId))?.savedViews?.active;
  // If the grid has special handling for no rows message for this view then show message
  if (props.viewNoShowMessage && props.viewNoShowMessage[activeView]) {
    return props.viewNoShowMessage[activeView];
  }
  return props.locale['common.noInformationToDisplay'];
};
let uniqueId = 0;

const resolver = (resolve) => setTimeout(resolve, 3000);

gluLocale.set(defaultStrings);
gluLocale.set(dateStrings);
gluLocale.set(popoverStrings);

export const BasicGridComponent = ({
  actionAfterExport,
  className,
  columnHeaderFilters,
  controlBarProps,
  customCsvPrefix,
  defaultColDef,
  defaultState,
  expandColumns,
  footerProps,
  gridId,
  headerLeftClasses,
  headerLeftContent,
  hideResults,
  label,
  lastSync,
  localeText,
  logPrint,
  onChange,
  onClearResults,
  onGridReady,
  onRowSelected,
  results,
  rowClassRules,
  serverSidePagination,
  showSavedViews,
  slideOut,
  sync,
  update,
  widget: { widgetName },
  ...props
}) => {
  const { allRows, gridContainer, gridRootOverride, printGrid, printHeader, printWidth } =
    useStyles();
  const locale = useLocale();

  const [allRecords, setAllRecords] = useState((props?.records || [])?.length);
  const [isPrint, setPrint] = useState(false);
  const [loadingData, setDataLoading] = useState(false);
  const [ariaPropsInitialised, setAriaPropsInitialised] = useState(false);
  const columnApi = useRef(null);
  const gridApi = useRef(null);
  const sectionId = useMemo(() => {
    uniqueId += 1;
    return `${gridId}-${uniqueId}`.replaceAll('-', '_').toLowerCase();
  }, [gridId]);
  const currentState = useRef(defaultState);
  const [excludedColumns, setExcludedColumns] = useState(EXCLUDE_FOR_EXPORT);
  const printContentRef = useRef(null);
  const onBeforeGetContentResolve = useRef(null);
  const userCode = useSelector(({ auth }) => auth.user?.userCode);
  const userName = useSelector(({ auth }) => auth.user?.userName);
  const user = userCode;
  const agGridId = gridId;
  const enablePopperScroll = !isIE();
  const [exportFileName, setExportFileName] = useState(customCsvPrefix);
  const printState = {
    page: 0,
    pageSize:
      allRecords > LIMIT_RECORDS_FOR_PRINT
        ? LIMIT_RECORDS_FOR_PRINT
        : allRecords || DEFAULT_PER_PAGE
  };
  const { height: windowHeight, width: windowWidth } = useWindowSize();

  const getActiveSavedView = () => {
    if (!agGridId) return null;

    return JSON.parse(
      sessionStorage.getItem(
        `${userCode}-${agGridId.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`)}`
      )
    )?.savedViews?.active;
  };

  const [activeSavedView, setActiveSavedView] = useState(getActiveSavedView());

  const getDefaultSavedView = () => {
    if (!agGridId) return null;

    const defaultSavedView = JSON.parse(
      sessionStorage.getItem(
        `${userCode}-${agGridId.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`)}`
      )
    )?.savedViews?.views?.filter((view) => view?.viewData?.isDefault);
    return defaultSavedView || [];
  };

  // determine if one of the static views is set to default in session storage and set it in the default state
  const getDefaultState = () => {
    const defaultSavedView = getDefaultSavedView();
    // if there is no default saved view in session storage then just return the defaultState with no changes
    if (!defaultSavedView.length > 0) return defaultState;

    // compare current item id to id of default saved view and if they match add isDefault: true
    // if they don't match add is default false
    const updatedViews = defaultState.savedViews?.views?.map((view) => {
      const updatedView = {
        ...view,
        viewData: {
          ...view.viewData,
          isDefault: view?.viewData?.id === defaultSavedView?.[0]?.viewData?.id
        }
      };
      return updatedView;
    }, []);

    return {
      ...defaultState,
      savedViews: { ...defaultState.savedViews, views: updatedViews }
    };
  };

  const onSync = useCallback(
    (...args) => loggedGridSync(args, { locale, sync, widgetName }),
    [sync, locale, widgetName]
  );

  const setPageChanges = (option) => {
    if (!gridApi.current || !currentState.current) return;

    const displayedRows = (gridApi.current.getModel()?.rowsToDisplay || []).length;
    setAllRecords(displayedRows);
    if (!serverSidePagination) {
      if (currentState.current.pageSize !== printState.pageSize)
        gridApi.current.paginationSetPageSize(option.pageSize);
      if (currentState.current.page !== printState.page)
        gridApi.current.paginationGoToPage(option.page);
    }
  };

  useEffect(() => {
    const checkboxWrapper = document.querySelector(
      `section[data-qa^="${sectionId}"] [ref="eWrapper"]`
    );
    checkboxWrapper?.removeAttribute('role');
    checkboxWrapper?.setAttribute('aria-hidden', true);

    const checkboxHeader = document.querySelector(
      `section[data-qa^="${sectionId}"] [role="columnheader"]`
    );

    if (checkboxHeader) {
      checkboxHeader.onkeydown = (e) => {
        if (e.code === SPACE_KEY_CODE) {
          setTimeout(() => {
            checkboxHeader?.focus();
          }, 500);
        }
      };
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ariaPropsInitialised) {
      const grid = document.querySelector(`section[data-qa^="${sectionId}"] [ref="gridBody"]`);
      if (grid) {
        const rowCount = grid.getAttribute('aria-rowcount');
        const colCount = grid.getAttribute('aria-colcount');
        grid.setAttribute('aria-label', label);
        grid.removeAttribute('role');
        grid.parentNode.setAttribute('role', 'grid');
        grid.parentNode.setAttribute(
          'aria-label',
          template(locale['common.grid.ariaLabel'])({
            colCount,
            rowCount,
            label
          })
        );
        setAriaPropsInitialised(true);
      }
    }
  });

  const gridActionButtons = [
    {
      dataQA: 'filterToggle',
      ariaLabel: locale['common.grid.filtersButton']
    },
    {
      dataQA: 'columnsToggle',
      ariaLabel: locale['common.grid.columnsToggle']
    },
    {
      dataQA: 'printer-icon',
      ariaLabel: locale['common.print']
    },
    {
      dataQA: 'exportButton',
      ariaLabel: locale['common.export']
    }
  ];

  const gridActionTitle = (dataQA, ariaLabel) => {
    const buttonAriaLabel = document.querySelector(
      `div[data-qa="controlBar"] svg[data-qa=${dataQA}]`
    );

    if (buttonAriaLabel) {
      buttonAriaLabel.parentElement.setAttribute('aria-label', `${ariaLabel}`);
    }
  };

  useEffect(() => {
    gridActionButtons.forEach(({ ariaLabel, dataQA }) => {
      gridActionTitle(dataQA, ariaLabel);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gridApi?.current) {
      // eslint-disable-next-line no-unused-expressions
      if (isPrint) {
        setPageChanges(printState);
        columnApi?.current?.autoSizeAllColumns();
      } else {
        setPageChanges(currentState.current);
        gridApi.current.sizeColumnsToFit();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi, isPrint]);

  useEffect(() => {
    if (gridApi?.current && expandColumns) gridApi.current.sizeColumnsToFit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi.current, expandColumns, windowWidth, windowHeight]);

  useEffect(() => {
    const currentApi = (gridApi || currentState)?.current;
    if (serverSidePagination && currentApi?.getDisplayedCount) {
      const records = currentApi.getDisplayedRowCount?.();
      if (!records) currentApi.showNoRowsOverlay();
    }
  }, [serverSidePagination, gridApi, currentState]);

  useEffect(() => {
    if (isPrint && printContentRef?.current && onBeforeGetContentResolve?.current) {
      resolver(onBeforeGetContentResolve.current);
    }
  }, [isPrint, printContentRef, onBeforeGetContentResolve]);

  const startPrint = useCallback(
    () =>
      new Promise((resolve) => {
        setPrint(true);
        setDataLoading(true);
        onBeforeGetContentResolve.current = resolve;
      }),
    []
  );

  const stopLoading = useCallback(
    () =>
      new Promise((resolve) => {
        setDataLoading(false);
        resolver(resolve);
      }),
    []
  );

  const stopPrint = useCallback(() => {
    setPrint(false);

    if (!logPrint) {
      logGridEvent(locale['common.print'], {
        eventMessage: locale['common.logPrintMessage'],
        userName,
        widgetName
      });
    } else {
      logPrint();
    }
  }, [logPrint, locale, widgetName, userName]);

  const handlePrint = useReactToPrint({
    content: () => printContentRef.current,
    onBeforeGetContent: () => startPrint(),
    onBeforePrint: () => stopLoading(),
    onAfterPrint: () => stopPrint()
  });

  const getTrigger = /* istanbul ignore next */ () => (
    <GetTrigger
      onClick={handlePrint}
      iconProps={{ 'data-qa': 'printer-icon' }}
      title={locale['common.print']}
    />
  );

  const onBasicGridChange = useCallback(
    (params) => {
      if (isPrint) return;

      currentState.current = {
        ...currentState.current,
        [params.name]: params.value
      };

      if (params.name === SAVED_VIEWS) {
        const active = activeSavedView || params?.value?.active;
        const currentSavedView = params?.value?.views?.find(
          (item) => item?.viewData?.id === active
        );

        currentState.current.pageSize = currentSavedView?.pageSize;

        // just to make the names prettier
        const convertedGridName = agGridId
          .replace(/-/g, ' ')
          .replace(
            /\w\S*/g,
            (txt) => `${txt.charAt(0).toUpperCase()}${txt.substr(1).toLowerCase()}`
          );
        const savedViewName = currentSavedView?.viewData?.name
          ? `-${currentSavedView?.viewData?.name}`
          : '';

        setExportFileName(`${customCsvPrefix || convertedGridName}${savedViewName}`);
        setActiveSavedView(null);
      }

      if (onChange) onChange(params, gridApi.current, columnApi.current);
    },
    [customCsvPrefix, onChange, isPrint, agGridId, activeSavedView]
  );

  const mapColumnPropsLocale = useMemo(() => mapColumnProps(locale), [locale]);

  const exportColumnConversion = useCallback((columns) => {
    setExcludedColumns(
      columns.reduce((colIds, { colDef, colId }) => {
        if (
          colDef?.disableExport ||
          colDef?.type === COLUMN_TYPES.ROW_SELECTOR ||
          colDef?.checkboxSelection
        ) {
          colIds.push(colId);
        }
        return colIds;
      }, EXCLUDE_FOR_EXPORT)
    );
  }, []);

  const onHideScreenModal = useCallback(() => slideOut.setOpen(false), [slideOut]);

  const actionAfterExportCallback = useCallback(
    () =>
      !actionAfterExport
        ? logGridEvent(locale['common.export'], {
            eventMessage: locale['common.logExportMessage'],
            userName,
            widgetName
          })
        : actionAfterExport(),
    [actionAfterExport, locale, userName, widgetName]
  );

  const modifyFiltersCallback = useCallback(
    (filters) => [...filters].sort((a, b) => (a.headerName > b.headerName ? 1 : -1)),
    []
  );

  const handleGridReady = ({ activeSavedView, expandColumns, onChange, onGridReady }) => {
    return (params) => {
      gridApi.current = params.api || {};
      columnApi.current = params.columnApi || {};
      const grid = document.querySelector(`section[data-qa^="${sectionId}"] [ref="gridBody"]`);
      if (!ariaPropsInitialised && grid) {
        grid.removeAttribute('role');
        gridApi.current.setGridAriaProperty('label', label);
        const rowCount = grid.getAttribute('aria-rowcount');
        const colCount = grid.getAttribute('aria-colcount');
        grid.parentNode.setAttribute('role', 'grid');
        grid.parentNode.setAttribute(
          'aria-label',
          template(locale['common.grid.ariaLabel'])({
            colCount,
            rowCount,
            label
          })
        );
        setAriaPropsInitialised(true);
      }
      onGridReady(params);
      if (expandColumns) {
        params.api.sizeColumnsToFit();
      }
      onChange({ activeSavedView, name: ON_GRID_READY }, params.api, params.columnApi);
      exportColumnConversion(params?.api?.csvCreator?.columnController?.displayedColumns || []);
    };
  };

  const convertToWhitespace = () => '\xa0';

  const parseCheckboxColDef = (item) => ({
    disableExport: true,
    filterable: false,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    headerName: locale['common.all'],
    initialWidth: 100,
    lockPosition: true,
    resizable: false,
    sortable: false,
    valueFormatter: convertToWhitespace,
    ...item
  });

  const parseColDef = (item) => {
    if (item?.cellClass) {
      const { cellClass, ...restColDef } = item;
      return {
        ...(!restColDef.type && !serverSidePagination
          ? { comparator: defaultStringComparator }
          : {}),
        cellClassRules: {
          [typeof cellClass === 'function'
            ? cellClass()
            : Array.isArray(cellClass)
            ? cellClass.join(' ')
            : cellClass]: () => true
        },
        ...restColDef
      };
    }
    return {
      ...(!item.type && !serverSidePagination ? { comparator: defaultStringComparator } : {}),
      ...(isPrint || item.cellRenderer === ACTIONS_COLUMN ? {} : { minWidth: 200, flex: 1 }),
      ...item
    };
  };

  const getPageStyle = () => `@page { size: landscape; margin: 0.25in }`;

  return (
    <div ref={printContentRef} className={classnames(printGrid, className)}>
      {isPrint && <style>{getPageStyle()}</style>}
      {isPrint && <PrintLogo />}
      {!isPrint && (
        <Snackbar
          dataQa="printSnackbar"
          show={loadingData}
          message={getPrintLoadingMsg({ allRecords, limit: LIMIT_RECORDS_FOR_PRINT, locale })}
        />
      )}
      {!hideResults &&
        results.map((result) => (
          <BasicResponseMessage
            onClearResults={onClearResults}
            result={result}
            key={result.title}
          />
        ))}
      {isPrint && widgetName && <h3 className={printHeader}>{widgetName}</h3>}
      {!isPrint && (
        <GridHeader headerLeftContent={headerLeftContent} headerLeftClasses={headerLeftClasses} />
      )}
      <GridActionModal {...props}>
        {({ modifiedBulkActions, modifiedColumns }) => {
          let defaultedColumns = (modifiedColumns || props.columnDefs || []).map(
            mapColumnPropsLocale
          );

          if (isPrint) {
            defaultedColumns = removeColumnsForPrint(defaultedColumns);
          }

          return (
            <section
              className={classnames(gridContainer, { [allRows]: isPrint, [printWidth]: isPrint })}
              data-qa={sectionId}
            >
              <Grid
                step={FLITER_VALUE_GRANULARITY}
                footerSticky={false}
                lastSync={moment.isMoment(lastSync) ? lastSync.format(DATE_TIME_FORMAT) : lastSync}
                classes={{
                  root: gridRootOverride
                }}
                syncData={onSync}
                customPrint={getTrigger}
                actionAfterExport={actionAfterExportCallback}
                modifyFilters={modifyFiltersCallback}
                gridId={kebabCase(agGridId)}
                sessionId={`${user}-${kebabCase(agGridId)}`}
                showSavedViews={showSavedViews}
                onGridReady={handleGridReady({
                  activeSavedView,
                  expandColumns,
                  onChange,
                  onGridReady
                })}
                onSavedViewsLoad={(params) => setActiveSavedView(params?.value?.active)}
                {...props}
                {...(modifiedBulkActions && {
                  bulkActions: modifiedBulkActions
                })}
                columnDefs={defaultedColumns?.map((item) =>
                  item?.checkboxSelection ? parseCheckboxColDef(item) : parseColDef(item)
                )}
                localeText={{
                  noRowsToShow: locale['common.noInformationToDisplay'],
                  ariaRowSelectAll: locale['common.toggleAllRowsSelection'],
                  ...localeText
                }}
                suppressPropertyNamesCheck
                rowSelection="multiple"
                domLayout={isPrint ? LAYOUT_KEYS.print : LAYOUT_KEYS.autoHeight}
                suppressColumnVirtualisation
                onChange={onBasicGridChange}
                onRowSelected={onRowSelected}
                PopoverProps={{
                  popperOptions: {
                    // This changed to Proptypes.objectOf(Proptypes.string) in glu/popover-react@4.0.3
                    // I believe this was an oversight, and should be looked at in Glu
                    // Getting Warning:
                    // "Failed prop type: Invalid prop `popperOptions.modifiers` of type `array` supplied to `Popover`, expected `string`"
                    modifiers: [
                      {
                        enabled: true,
                        name: 'eventListeners',
                        options: {
                          // Disable scroll on internet explorer
                          scroll: enablePopperScroll
                        }
                      }
                    ]
                  }
                }}
                defaultColDef={{
                  cellRendererParams: {
                    alwaysUseActionsPopover: true,
                    type: 'ellipsis'
                  },
                  resizable: true,
                  ...(isPrint && {
                    maxWidth: 450
                  }),
                  ...defaultColDef
                }}
                defaultState={isPrint && !serverSidePagination ? printState : getDefaultState()}
                disablePinning
                {...(isPrint ? EXTRA_PRINT_PROPS : {})}
                rowClassRules={{
                  ...rowClassRules,
                  'ag-row-error': /* istanbul ignore next */ ({ data }) => data.errorHighlighting,
                  'ag-row-success': /* istanbul ignore next */ ({ data }) =>
                    data.successHighlighting
                }}
                // TODO: Below has casing incorrect, should be FooterProps - to keep custom pagination page size TBD
                footerProps={{
                  parsePageSizeOptions: limitPageCount(locale),
                  ...(footerProps && footerProps)
                }}
                noRowsOverlayComponent="customNoRowsOverlay"
                noRowsOverlayComponentParams={{
                  locale,
                  sessionId: `${user}-${kebabCase(agGridId)}`,
                  ...props?.noRowsOverlayComponentParams
                }}
                frameworkComponents={{
                  customNoRowsOverlay: NoRowsOverlayComponent,
                  ...props?.frameworkComponents
                }}
                ControlBarProps={{
                  MultiselectFilterProps: { filterEnabled: true },
                  excludeForExport: excludedColumns,
                  exportFileName,
                  fetchSavedViews,
                  saveView,
                  ...(isPrint ? { showControlBar: false } : {}),
                  ...(controlBarProps && controlBarProps)
                }}
                columnHeaderFilters={columnHeaderFilters}
                serverSidePagination={serverSidePagination}
              />
            </section>
          );
        }}
      </GridActionModal>
      {Boolean(slideOut.setOpen) && (
        <FullscreenModal
          show={slideOut.open}
          header={slideOut.title}
          icon="info"
          onHide={onHideScreenModal}
        >
          {slideOut.content}
        </FullscreenModal>
      )}
    </div>
  );
};

BasicGridComponent.propTypes = {
  columnDefs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columnHeaderFilters: PropTypes.bool,
  columnsToExport: PropTypes.arrayOf(PropTypes.shape({})),
  controlBarProps: PropTypes.shape({}),
  customCsvPrefix: PropTypes.string,
  expandColumns: PropTypes.bool,
  exportDataConversion: PropTypes.func,
  exportSelectId: PropTypes.string,
  footerProps: PropTypes.shape({}),
  frameworkComponents: PropTypes.objectOf(PropTypes.func),
  defaultState: PropTypes.shape({
    savedViews: PropTypes.shape({
      views: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }),
  gridId: PropTypes.string.isRequired,
  defaultColDef: PropTypes.shape({}),
  headerLeftClasses: JSSClasses,
  actionAfterExport: PropTypes.oneOfType([PropTypes.func, PropTypes.instanceOf(null)]),
  headerLeftContent: PropTypes.node,
  className: PropTypes.string,
  hideResults: PropTypes.bool,
  label: PropTypes.string.isRequired,
  lastSync: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  localeText: PropTypes.objectOf(PropTypes.string),
  logPrint: PropTypes.oneOfType([PropTypes.func, PropTypes.instanceOf(null)]),
  noRowsOverlayComponentParams: PropTypes.shape({}),
  onChange: PropTypes.func,
  onClearResults: PropTypes.func,
  onGridReady: PropTypes.func,
  onRowSelected: PropTypes.func,
  records: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  results: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})),
      displayData: PropTypes.arrayOf(
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func]))
      ),
      name: PropTypes.string.isRequired,
      title: PropTypes.string,
      variant: PropTypes.string
    })
  ),
  rowClassRules: PropTypes.shape({}),
  savedViews: PropTypes.shape({}),
  serverSidePagination: PropTypes.bool,
  setSlideOutOpen: PropTypes.func,
  showSavedViews: PropTypes.bool,
  slideOut: PropTypes.shape({
    content: PropTypes.element,
    open: PropTypes.oneOf([PropTypes.bool, PropTypes.number]),
    setOpen: PropTypes.func,
    title: PropTypes.string
  }),
  sync: PropTypes.func,
  update: PropTypes.func,
  widget: PropTypes.shape({
    widgetName: PropTypes.string
  })
};

BasicGridComponent.defaultProps = {
  columnHeaderFilters: false,
  columnsToExport: [],
  controlBarProps: {},
  customCsvPrefix: '',
  expandColumns: false,
  defaultState: {},
  exportDataConversion: null,
  exportSelectId: 'id',
  footerProps: null,
  frameworkComponents: {},
  defaultColDef: {},
  headerLeftClasses: null,
  actionAfterExport: null,
  headerLeftContent: null,
  className: '',
  hideResults: false,
  lastSync: '',
  localeText: {},
  logPrint: null,
  noRowsOverlayComponentParams: {},
  onChange() {},
  onClearResults() {},
  onGridReady() {},
  onRowSelected() {},
  results: [],
  rowClassRules: {},
  savedViews: {},
  serverSidePagination: false,
  setSlideOutOpen: () => {},
  showSavedViews: true,
  slideOut: {},
  sync: null,
  update() {},
  widget: {
    widgetName: ''
  }
};
/* istanbul ignore next */

export default withWidget(BasicGridComponent);
